import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authAPI } from "../services/auth.service";

// Async thunks
export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(email, password);
      if (response.success) {
        return response.user;
      }
      return rejectWithValue(response.message || "Login failed");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Invalid email or password"
      );
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.logout();
      return null;
    } catch (error) {
      console.error("Logout error:", error);
      // Still clear the user even if API call fails
      return null;
    }
  }
);

export const checkAuth = createAsyncThunk(
  "auth/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      const storedUser = localStorage.getItem("currentUser");
      const accessToken = localStorage.getItem("accessToken");

      if (!storedUser || !accessToken) {
        return rejectWithValue("No stored user");
      }

      // Verify token is still valid
      const response = await authAPI.getMe();
      if (response.success) {
        localStorage.setItem("currentUser", JSON.stringify(response.user));
        return response.user;
      }

      return rejectWithValue("Token invalid");
    } catch (error) {
      // Token invalid, clear auth
      localStorage.removeItem("currentUser");
      localStorage.removeItem("accessToken");
      return rejectWithValue("Authentication check failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    loading: true,
    error: null,
    isAuthenticated: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
      })
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
      });
  },
});

// Selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;

// Helper selectors
export const selectHasRole = (state, role) => {
  return state.auth.user?.role === role;
};

export const selectIsPanelAdmin = (state, panelId = null) => {
  const user = state.auth.user;
  if (!user || !user.isPanelAdmin) return false;
  if (panelId) {
    return user.adminForPanels?.includes(panelId);
  }
  return true;
};

export const { clearError, setUser } = authSlice.actions;

export default authSlice.reducer;
